(async () => {
  "use strict";

  /* -------------------------------------------------------
   * Safe Logging (no breaking older browsers)
   * ----------------------------------------------------- */
  const safeLog = (...a) => { try { console.log(...a); } catch {} };
  const safeWarn = (...a) => { try { console.warn(...a); } catch {} };
  const safeError = (...a) => { try { console.error(...a); } catch {} };

  /* -------------------------------------------------------
   * Read product info from DOM
   * ----------------------------------------------------- */
  const readProductHandle = () => {
    try {
      const el = document.getElementById("product-handle");
      return el ? JSON.parse(el.textContent) : null;
    } catch (err) {
      safeWarn("Failed to read product handle", err);
      return null;
    }
  };

  const readProductTitle = () => {
    try {
      return document.getElementById("product-title")?.textContent.trim() || "";
    } catch {
      return "";
    }
  };

  /* -------------------------------------------------------
   * Shopify GraphQL
   * ----------------------------------------------------- */
  const GRAPHQL_URL = "https://autumn-and-may.myshopify.com/api/2025-01/graphql.json";
  const STOREFRONT_TOKEN = "d93cdf1eda452a4ac6bd403e2f7b948d";

  const VARIANTS_PAGE_QUERY = `
    query productVariantsPage($handle: String!, $pageSize: Int!, $after: String) {
      productByHandle(handle: $handle) {
        id
        title
        variants(first: $pageSize, after: $after) {
          edges {
            cursor
            node {
              id
              title
              sku
              availableForSale
              selectedOptions { name value }
              image { url altText }
              priceV2 { amount currencyCode }
              compareAtPriceV2 { amount currencyCode }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  const fetchPage = async (handle, pageSize = 250, after = null) => {
    try {
      const resp = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(STOREFRONT_TOKEN && {
            "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
          }),
        },
        body: JSON.stringify({
          query: VARIANTS_PAGE_QUERY,
          variables: { handle, pageSize, after },
        }),
        credentials: "same-origin",
      });

      if (!resp.ok) return null;
      const json = await resp.json();
      //console.log(json);
      return json?.data?.productByHandle || null;
    } catch {
      return null;
    }
  };

  const fetchAllVariantsByHandle = async (handle) => {
    const all = [];
    let after = null;

    for (let i = 0; i < 1000; i++) {
      const product = await fetchPage(handle, 250, after);
      if (!product) break;

      const edges = product.variants?.edges || [];
      edges.forEach((e) => e?.node && all.push(e.node));

      const info = product.variants?.pageInfo;
      if (!info?.hasNextPage) break;

      after = info.endCursor;
    }

    return all;
  };

  /* -------------------------------------------------------
   * Transform raw Shopify variant → normalized object
   * ----------------------------------------------------- */
  const transformVariant = (node, productTitle) => {
    const opts = node.selectedOptions?.map((o) => o.value) || [];
    const [option1, option2, option3] = opts;

    const title = opts.filter(Boolean).join(" / ");

    const img = node.image
      ? {
          src: node.image.url,
          alt: node.image.altText || null,
        }
      : null;

    return {
      id: Number(node.id.replace("gid://shopify/ProductVariant/", "")),
      title,
      option1,
      option2,
      option3,
      sku: node.sku || null,
      requires_shipping: true,
      taxable: true,
      featured_image: img
        ? {
            src: img.src,
            alt: img.alt,
            id: null,
            product_id: null,
            position: null,
            created_at: null,
            updated_at: null,
            width: null,
            height: null,
            variant_ids: [],
          }
        : null,
      featured_media: img
        ? {
            id: null,
            alt: img.alt,
            position: 1,
            preview_image: {
              src: img.src,
              aspect_ratio: 1,
              width: null,
              height: null,
            },
          }
        : null,
      available: node.availableForSale,
      name: `${productTitle} - ${title}`,
      public_title: title,
      options: opts.filter(Boolean),
      price: Math.round(Number(node.priceV2.amount) * 100),
      compare_at_price: node.compareAtPriceV2
        ? Math.round(Number(node.compareAtPriceV2.amount) * 100)
        : null,
      weight: 454,
      inventory_management: "shopify",
      barcode: null,
      requires_selling_plan: false,
      selling_plan_allocations: [],
    };
  };

  /* -------------------------------------------------------
   * Render Dynamic Variant Picker
   * ----------------------------------------------------- */
  const renderVariantPicker = (selectId = "ProductSelect-product-template") => {
    const container = document.getElementById(selectId);
    if (!container) return;

    container.innerHTML = "";

    const optionsKeys = ["option1", "option2", "option3"].filter((key) =>
      window.ALL_VARIANTS.some((v) => v[key])
    );

    optionsKeys.forEach((key) => {
      const sel = document.createElement("select");
      sel.id = `Option-${key}`;
      sel.name = `options[${key}]`;
      sel.dataset.optionKey = key;

      const values = [
        ...new Set(window.ALL_VARIANTS.map((v) => v[key]).filter(Boolean)),
      ];

      values.forEach((val) => {
        const opt = document.createElement("option");
        opt.value = val;
        opt.textContent = val;
        sel.appendChild(opt);
      });

      const wrapper = document.createElement("div");
      wrapper.className = "product-option-wrapper";

      const label = document.createElement("label");
      label.textContent = key;

      wrapper.append(label, sel);
      container.appendChild(wrapper);
    });

    safeLog(
      `Rendered ${window.ALL_VARIANTS.length} variants with ${optionsKeys.length} option selects`
    );
  };

  /* -------------------------------------------------------
   * Main
   * ----------------------------------------------------- */
  document.addEventListener("DOMContentLoaded", async () => {
    const handle = readProductHandle();
    const title = readProductTitle() || "Product";

    if (!handle) {
      safeWarn("No product handle found");
      return;
    }

    const variants = await fetchAllVariantsByHandle(handle);
    const transformed = variants.map((v) => transformVariant(v, title));

    window.ALL_VARIANTS = transformed;
    window.VARIANT_BY_ID = Object.fromEntries(
      transformed.map((v) => [v.id, v])
    );
    window.TPS_VARIANTS_READY = true;

    // Dispatch readiness event
    try {
      window.dispatchEvent(
        new CustomEvent("tps:variants:ready", {
          detail: { count: transformed.length },
        })
      );
    } catch {
      const evt = document.createEvent("Event");
      evt.initEvent("tps:variants:ready", true, false);
      window.dispatchEvent(evt);
    }

    renderVariantPicker();
  });
})();
